<div class="tickets-container">
  <!-- Professional Header Section - Matching Change Order Style -->
  <div class="tickets-header-section">
    <div class="tickets-controls">
      <div class="search-section">
        <mat-icon>search</mat-icon>
        <input type="search"
          [(ngModel)]="search"
          [ngModelOptions]="{ standalone: true }"
          (ngModelChange)="onSearch()"
          placeholder="Search tickets..."
          class="search-input">
      </div>

      @if (record_id) {
        <button class="btn-add-ticket" (click)="createTicket()" [appPermission]="createPermission"
          [disabled]="isLocked" [ngClass]="{'permission-disabled': isLocked}">
          <mat-icon>add</mat-icon>
          <span>Add Ticket</span>
        </button>
      }

      <div class="view-toggle">
        <button class="toggle-btn"
          [class.activeBtn]="viewMode === 'grid'"
          (click)="setViewMode('grid')"
          matTooltip="Grid View">
          <mat-icon>grid_view</mat-icon>
        </button>
        <button class="toggle-btn"
          [class.activeBtn]="viewMode === 'list'"
          (click)="setViewMode('list')"
          matTooltip="List View">
          <mat-icon>view_list</mat-icon>
        </button>
      </div>
    </div>
  </div>

  <!-- Grid View - Enhanced Cards Matching Change Order -->
  @if (viewMode === 'grid' && !isLoading) {
    <div class="tickets-grid-view">
      @for (ticket of ticketList; track trackByItemId($index, ticket)) {
        <div class="ticket-card-enhanced"
          [class.high-priority]="ticket.priorityLevel === 'high'"
          [class.urgent]="ticket.isAging && !(ticket.status_label?.name?.toLowerCase()?.includes('closed'))">
          <!-- Progress Strip (Dynamic) -->
          <div class="ticket-progress-strip">
            <div class="ticket-progress-fill"
              [ngClass]="getProgressColorClass(ticket.approvalProgress)"
              [style.width.%]="ticket.approvalProgress">
            </div>
          </div>
          <!-- Card Header -->
          <div class="card-header-section">
            <div class="ticket-identifier">
              <mat-icon>confirmation_number</mat-icon>
              <div class="ticket-details">
                <div class="ticket-header-row">
                  <h3 (click)="openTicketDetail(ticket.id)"
                    [appPermission]="viewDetailPermission"
                    class="ticket-title">
                    {{ticket.name}}
                  </h3>
                </div>
                <span class="ticket-code">{{ticket.code}}</span>
              </div>
            </div>
            <div class="badge-status-container">
              @if (ticket.isNew) {
                <span class="badge-new">NEW</span>
              }
              <app-status-pill variant="solid"
                [color]="ticket.status_label?.color ?? null"
                [label]="ticket.status_label?.name ?? null"></app-status-pill>
            </div>
            <!-- Header Actions Bottom with Tags and Toggle Buttons -->
            <div class="header-actions-bottom">
              <!-- Tags Section (Left Side) -->
              @if (ticket.processedTags && ticket.processedTags.length > 0) {
                <div class="tags-section">
                  <div class="tags-list">
                    @for (tag of ticket.processedTags; track tag) {
                      <span class="tag-chip"
                        [style.background-color]="getTagBackgroundColor(tag.color || tag.tag_color || '')"
                        [style.color]="getTagTextColor(tag.color || tag.tag_color || '')"
                        [matTooltip]="tag.name || tag.tag || ''">
                        {{tag.name || tag.tag}}
                      </span>
                    }
                  </div>
                </div>
              }
              <!-- Toggle Buttons (Right Side) -->
              <div class="toggle-buttons-group">
                <button class="section-toggle-btn"
                  [class.active]="ticket.showInfo"
                  (click)="toggleInfoSection(ticket, $event)"
                  matTooltip="Toggle Info">
                  <mat-icon>info</mat-icon>
                </button>
                <button class="section-toggle-btn"
                  [class.active]="ticket.showDetails"
                  (click)="toggleDetailsSection(ticket, $event)"
                  matTooltip="Toggle Details">
                  <mat-icon>analytics</mat-icon>
                </button>
                <button class="section-toggle-btn"
                  [class.active]="ticket.showActivity"
                  (click)="toggleActivitySection(ticket, $event)"
                  matTooltip="Toggle Activity">
                  <mat-icon>history</mat-icon>
                </button>
              </div>
            </div>
          </div>
          <!-- Info Section (Collapsible) -->
          @if (ticket.showInfo) {
            <div class="info-section"
              (click)="openTicketDetail(ticket.id)"
              [appPermission]="viewDetailPermission">
              <div class="section-header">
                <mat-icon>info</mat-icon>
                <span>Information</span>
              </div>
              <div class="info-grid">
                <div class="info-item">
                  <span class="info-label">Created</span>
                  <span class="info-value">{{ticket.createdDaysAgo}}</span>
                </div>
                <div class="info-item">
                  <span class="info-label">Type</span>
                  <span class="info-value">{{ticket.ticket_type?.name || 'N/A'}}</span>
                </div>
                @if (ticket.due_date) {
                  <div class="info-item">
                    <span class="info-label">Due Date</span>
                    <span class="info-value" [class.overdue]="ticket.isOverdue">{{ticket.formattedDueDate}}</span>
                  </div>
                }
                @if (ticket.hasDescription) {
                  <div class="info-item">
                    <span class="info-label">Description</span>
                    <span class="info-value" [matTooltip]="ticket.description || ''">{{ticket.truncatedDescription}}</span>
                  </div>
                }
              </div>
            </div>
          }
          <!-- Details Section (Collapsible) -->
          @if (ticket.showDetails) {
            <div class="details-section"
              (click)="openTicketDetail(ticket.id)"
              [appPermission]="viewDetailPermission">
              <div class="metric-row">
                <div class="metric-item">
                  <mat-icon>{{getPriorityIcon(ticket.priorityLevel)}}</mat-icon>
                  <div class="metric-content">
                    <span class="metric-label">Priority</span>
                    <span class="metric-value" [class.high-priority]="ticket.priorityLevel === 'high'">
                      {{ticket.priority_label || 'Normal'}}
                    </span>
                  </div>
                </div>
                <div class="metric-item">
                  <mat-icon>people</mat-icon>
                  <div class="metric-content">
                    <span class="metric-label">Assigned To</span>
                    <span class="metric-value"
                      [matTooltip]="concatString(ticket.responsible_persons, 'full_name') || concatString(ticket.responsible_persons, 'name')">
                      {{ticket.assigneeDisplayText}}
                      @if (ticket.responsible_persons?.length === 1 && ticket.responsible_persons[0]?.type) {
                        <span class="assignee-type">
                          ({{ticket.responsible_persons[0].type}})
                        </span>
                      }
                    </span>
                  </div>
                </div>
                @if (ticket.party_name) {
                  <div class="metric-item">
                    <mat-icon>business</mat-icon>
                    <div class="metric-content">
                      <span class="metric-label">Responsible Party</span>
                      <span class="metric-value">{{ticket.party_name}}</span>
                    </div>
                  </div>
                }
                @if (ticket.hasScopes) {
                  <div class="metric-item">
                    <mat-icon>category</mat-icon>
                    <div class="metric-content">
                      <span class="metric-label">Scopes</span>
                      <span class="metric-value" [matTooltip]="concatString(ticket.scopes, 'name')">
                        {{ticket.scopeDisplayText}}
                      </span>
                    </div>
                  </div>
                }
                <div class="metric-item">
                  <mat-icon>schedule</mat-icon>
                  <div class="metric-content">
                    <span class="metric-label">Age</span>
                    <span class="metric-value" [class.aging]="ticket.isAging">{{ticket.ageInDays}} days</span>
                  </div>
                </div>
              </div>
            </div>
          }
          <!-- Activity Section (Collapsible) -->
          @if (ticket.showActivity) {
            <div class="activity-section"
              (click)="openTicketDetail(ticket.id)"
              [appPermission]="viewDetailPermission">
              <div class="section-header">
                <mat-icon>history</mat-icon>
                <span>Activity</span>
              </div>
              <div class="activity-items">
                <div class="activity-item">
                  <span class="item-label">Days in Status</span>
                  <span class="item-value">{{ticket.daysInStatus}}</span>
                </div>
                @if (ticket.created_by) {
                  <div class="activity-item">
                    <span class="item-label">Created By</span>
                    <span class="item-value">{{ticket.created_by?.full_name || 'System'}}</span>
                  </div>
                }
              </div>
            </div>
          }
          <!-- Smart Warning Indicators -->
          @if (ticket.requiresAttention || ticket.isOverdue || ticket.billable_label) {
            <div class="warning-section">
              @if (ticket.requiresAttention) {
                <div class="warning-item warning-attention">
                  <mat-icon>warning</mat-icon>
                  <span>Needs Attention</span>
                </div>
              }
              @if (ticket.isOverdue) {
                <div class="warning-item warning-overdue">
                  <mat-icon>schedule</mat-icon>
                  <span>Overdue</span>
                </div>
              }
              @if (ticket.billable_label) {
                <div class="warning-item warning-billable"
                  [matTooltip]="'Billing: ' + ticket.billable_label">
                  <mat-icon>payment</mat-icon>
                  <span>{{ticket.billable_label}}</span>
                </div>
              }
            </div>
          }
          <!-- Card Actions -->
          <div class="card-actions">
            <button class="action-btn primary"
              (click)="openTicketDetail(ticket.id)"
              [appPermission]="viewDetailPermission">
              <mat-icon>visibility</mat-icon>
              <span>View Details</span>
            </button>
            <button class="action-btn"
              (click)="generatePDF(ticket.id)"
              [appPermission]="exportPDFPermission">
              <mat-icon>picture_as_pdf</mat-icon>
              <span>Export</span>
            </button>
            <button class="action-btn icon-only"
              [matMenuTriggerFor]="menu">
              <mat-icon>more_vert</mat-icon>
            </button>
            <mat-menu #menu="matMenu">
              <button mat-menu-item (click)="openTicketDetail(ticket.id)"
                [appPermission]="viewDetailPermission">
                <mat-icon>open_in_new</mat-icon>
                <span>Open in New Tab</span>
              </button>
              <button mat-menu-item (click)="generatePDF(ticket.id)"
                [appPermission]="exportPDFPermission">
                <mat-icon>picture_as_pdf</mat-icon>
                <span>Export as PDF</span>
              </button>
            </mat-menu>
          </div>
        </div>
      }
    </div>
  }

  <!-- List View - Professional Table -->
  @if (viewMode === 'list' && !isLoading) {
    <div class="tickets-list-view">
      <table class="tickets-table-enhanced">
        <thead>
          <tr>
            <th class="col-ticket-number">Ticket #</th>
            <th>Name</th>
            <th>Description</th>
            <th>Type</th>
            <th class="col-priority">Priority</th>
            <th class="col-status">Status</th>
            <th>Assigned To</th>
            <th>Responsible Party</th>
            <th>Scopes</th>
            <th class="col-tags">Tags</th>
            <th>Created</th>
            <th>Progress</th>
            <th class="col-actions">Actions</th>
          </tr>
        </thead>
        <tbody>
          @for (ticket of ticketList; track trackByItemId($index, ticket)) {
            <tr
              class="ticket-row"
              [class.high-priority]="ticket.priorityLevel === 'high'"
              [style.border-left]="'4px solid ' + ticket.status_label?.color"
              (click)="openTicketDetail(ticket.id)"
              [appPermission]="viewDetailPermission">
              <td class="ticket-number-cell">
                <div class="ticket-id-wrapper">
                  <mat-icon>confirmation_number</mat-icon>
                  <strong>{{ticket.code}}</strong>
                  @if (ticket.isNew) {
                    <span class="badge-new">NEW</span>
                  }
                </div>
              </td>
              <td>
                <span class="ticket-name-text">{{ticket.name}}</span>
              </td>
              <td>
                @if (ticket.hasDescription) {
                  <span class="description-cell" [matTooltip]="ticket.description || ''">
                    {{ticket.truncatedDescription}}
                  </span>
                }
                @if (!ticket.hasDescription) {
                  <span class="no-description">-</span>
                }
              </td>
              <td>
                <span>{{ticket.ticket_type?.name || 'N/A'}}</span>
              </td>
              <td class="priority-cell">
                <span class="priority-badge" [class.high-priority]="ticket.priorityLevel === 'high'">
                  <mat-icon class="priority-icon-small">{{getPriorityIcon(ticket.priorityLevel)}}</mat-icon>
                  {{ticket.priority_label || 'Normal'}}
                </span>
                @if (ticket.billable_label) {
                  <span class="billable-indicator"
                    [matTooltip]="'Billing: ' + ticket.billable_label">
                    <mat-icon>payment</mat-icon>
                  </span>
                }
              </td>
              <td>
                <app-status-pill variant="solid"
                  [color]="ticket.status_label?.color ?? null"
                  [label]="ticket.status_label?.name ?? null"></app-status-pill>
              </td>
              <td class="assignee-cell">
                @if (ticket.responsible_persons?.length) {
                  <div class="assignee-inline">
                    <span class="assignee-name-text">
                      {{ticket.responsible_persons[0]?.full_name || ticket.responsible_persons[0]?.name || 'Unknown' }}
                    </span>
                    @if (ticket.responsible_persons?.length === 1 && ticket.responsible_persons[0]?.type) {
                      <span class="assignee-type-small">
                        ({{ticket.responsible_persons[0].type}})
                      </span>
                    }
                    @if (ticket.hasMultipleAssignees) {
                      <span class="more-count-badge"
                        [matTooltip]="concatString(ticket.responsible_persons, 'full_name') || concatString(ticket.responsible_persons, 'name')">
                        +{{ticket.responsible_persons.length - 1}}
                      </span>
                    }
                  </div>
                }
                @if (!ticket.responsible_persons?.length) {
                  <span class="no-party">Unassigned</span>
                }
              </td>
              <td>
                @if (ticket.party_name) {
                  <span class="party-name-text">
                    {{ticket.party_name}}
                  </span>
                }
                @if (!ticket.party_name) {
                  <span class="no-party">-</span>
                }
              </td>
              <td>
                <span [matTooltip]="concatString(ticket.scopes, 'name')" class="scope-text">
                  {{ticket.scopeDisplayText}}
                </span>
              </td>
              <td class="tags-cell">
                @if (ticket.processedTags && ticket.processedTags.length > 0) {
                  <div class="tags-inline">
                    @for (tag of ticket.processedTags | slice:0:1; track tag) {
                      <span class="tag-chip-small"
                        [style.background-color]="getTagBackgroundColor(tag.color || tag.tag_color || '')"
                        [style.color]="getTagTextColor(tag.color || tag.tag_color || '')"
                        [matTooltip]="tag.name || tag.tag || ''">
                        {{tag.name || tag.tag}}
                      </span>
                    }
                    @if (ticket.processedTags.length > 1) {
                      <span class="more-count-badge-wrapper">
                        <span class="more-count-badge">+{{ticket.processedTags.length - 1}}</span>
                        <div class="tags-popover">
                          @for (tag of ticket.processedTags | slice:1; track tag) {
                            <span class="tag-chip-small"
                              [style.background-color]="getTagBackgroundColor(tag.color || tag.tag_color || '')"
                              [style.color]="getTagTextColor(tag.color || tag.tag_color || '')">
                              {{tag.name || tag.tag}}
                            </span>
                          }
                        </div>
                      </span>
                    }
                  </div>
                }
                @if (!ticket.processedTags || ticket.processedTags.length === 0) {
                  <span class="no-tags">-</span>
                }
              </td>
              <td>
                <span [class.aging]="ticket.isAging">
                  {{ticket.formattedDueDate}}
                  @if (ticket.isOverdue) {
                    <span class="badge-overdue">OVERDUE</span>
                  }
                </span>
              </td>
              <td>
                <div class="progress-mini">
                  <div class="progress-mini-bar">
                    <div class="progress-mini-fill"
                      [style.width.%]="ticket.approvalProgress"></div>
                  </div>
                  <span class="progress-text">{{ticket.approvalProgress}}%</span>
                </div>
              </td>
              <td class="actions-cell" (click)="$event.stopPropagation()">
                <button class="btn-icon-small"
                  (click)="openTicketDetail(ticket.id)"
                  matTooltip="View Details"
                  [appPermission]="viewDetailPermission">
                  <mat-icon>visibility</mat-icon>
                </button>
                <button class="btn-icon-small"
                  (click)="generatePDF(ticket.id)"
                  matTooltip="Export PDF"
                  [appPermission]="exportPDFPermission">
                  <mat-icon>picture_as_pdf</mat-icon>
                </button>
                <button class="btn-icon-small"
                  [matMenuTriggerFor]="menuList">
                  <mat-icon>more_vert</mat-icon>
                </button>
                <mat-menu #menuList="matMenu">
                  <button mat-menu-item (click)="openTicketDetail(ticket.id)"
                    [appPermission]="viewDetailPermission">
                    <mat-icon>open_in_new</mat-icon>
                    <span>Open in New Tab</span>
                  </button>
                  <button mat-menu-item (click)="generatePDF(ticket.id)"
                    [appPermission]="exportPDFPermission">
                    <mat-icon>picture_as_pdf</mat-icon>
                    <span>Export as PDF</span>
                  </button>
                </mat-menu>
              </td>
            </tr>
          }
        </tbody>
      </table>
    </div>
  }

  <!-- Empty State -->
  @if (!isLoading && (!ticketList || ticketList.length === 0)) {
    <div class="empty-state">
      <mat-icon>confirmation_number</mat-icon>
      <h3>No Tickets Found</h3>
      <p>{{ search ? 'Try adjusting your search criteria' : 'Create your first ticket to get started' }}</p>
      @if (!search && record_id) {
        <button class="btn-add-ticket"
          (click)="createTicket()"
          [appPermission]="createPermission"
          [disabled]="isLocked" [ngClass]="{'permission-disabled': isLocked}">
          <mat-icon>add</mat-icon>
          <span>Create Ticket</span>
        </button>
      }
    </div>
  }

  <!-- Loading State -->
  @if (isLoading) {
    <div class="loading-state">
      <mat-spinner diameter="40"></mat-spinner>
      <p>Loading tickets...</p>
    </div>
  }

  <!-- Load More -->
  @if (showLoadMoreOption && !isLoadingMore) {
    <div class="load-more-wrapper">
      <button class="load-more-btn" (click)="loadMoreData()">
        <mat-icon>expand_more</mat-icon>
        <span>Load More</span>
      </button>
    </div>
  }

  <!-- Loading More State -->
  @if (isLoadingMore) {
    <div class="loading-more">
      <mat-spinner diameter="24"></mat-spinner>
      <span>Loading more tickets...</span>
    </div>
  }
</div>